import { useState } from 'react';

import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQuery } from 'react-query';

import { fetchTimelineAnalysis } from '../services';
import { isTimelineDateAfterToday } from '../timelineData';
import {
  formatTimelineDate,
  getTimelineOperationPath,
  getTimelineRecordSummary,
  getTimelineRecordTitle,
  normalizePoints,
} from '../timelinePresentation';

import type { TimelineAnalysis } from '../types';
import type { TimelineRecord } from 'src/types/convocation-summary';

export const timelineDetailsActionGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'minmax(0, 1.3fr) minmax(0, 1fr)',
  },
  gap: 1,
} as const;

export const timelineDetailsActionButtonSx = {
  minWidth: 0,
  px: 1,
  whiteSpace: { xs: 'normal', sm: 'nowrap' },
  overflowWrap: 'anywhere',
  textTransform: 'none',
} as const;

type TimelineAnalysisStatus = 'success' | 'error' | 'loading';

interface TimelineDetailsDrawerProps {
  open: boolean;
  record: TimelineRecord | null;
  navbarAdditionalHeight: number;
  onClose: () => void;
  operationId?: string;
  operationScoped?: boolean;
}

interface DetailFact {
  label: string;
  value?: string | number | null;
  href?: string;
}

interface TimelineDetailsContentProps {
  record: TimelineRecord;
  analysis: TimelineAnalysis | undefined;
  analysisStatus: TimelineAnalysisStatus;
  operationScoped: boolean;
  summary?: string;
  copyFeedbackMessage: string;
  onCopySummary: () => void;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const getHttpUrl = (value: unknown) => {
  const candidate = normalizePoints(value);
  if (!candidate) return undefined;

  try {
    const parsed = new URL(candidate);
    return ['https:', 'http:'].includes(parsed.protocol)
      ? parsed.href
      : undefined;
  } catch {
    return undefined;
  }
};

const getArrayValue = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getDetailPoints = (value: unknown) =>
  getArrayValue(value)
    .map((item, index) => {
      if (!isObject(item)) return undefined;
      const content = normalizePoints(item.conteudo);
      if (!content) return undefined;

      return {
        title: normalizePoints(item.titulo) || `Ponto relevante ${index + 1}`,
        content,
      };
    })
    .filter((item): item is { title: string; content: string } =>
      Boolean(item),
    );

const getCitations = (value: unknown) =>
  getArrayValue(value)
    .map((item) => {
      if (!isObject(item)) return undefined;
      const excerpt = normalizePoints(item.trecho);
      const url = getHttpUrl(item.url);
      const page = normalizePoints(item.pagina);
      const status = normalizePoints(item.status);
      if (!excerpt && !url) return undefined;

      return { excerpt, url, page, status };
    })
    .filter(
      (
        item,
      ): item is {
        excerpt: string | undefined;
        url: string | undefined;
        page: string | undefined;
        status: string | undefined;
      } => Boolean(item),
    );

const getCitationStatusKey = (status?: string) =>
  status?.trim().toLocaleLowerCase('pt-BR').replace(/_/g, ' ');

const formatCitationStatus = (status?: string) => {
  const normalized = getCitationStatusKey(status);
  if (normalized === 'exata') return 'Citação exata';
  if (normalized === 'aproximada') return 'Citação aproximada';
  if (normalized === 'nao localizada' || normalized === 'não localizada') {
    return 'Citação não localizada';
  }
  return status || 'Citação';
};

const getCitationCount = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.replace(',', '.'));
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return undefined;
};

const getValueText = (...values: unknown[]) => {
  for (const value of values) {
    const text = normalizePoints(value);
    if (text) return text;
  }

  return undefined;
};

const formatCnpj = (value?: string | null) => {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14) return value;

  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

const TimelineDetailsSkeleton = () => (
  <Stack
    spacing={2.5}
    data-testid="timeline-details-skeleton"
    aria-hidden="true">
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Skeleton variant="rounded" width={110} height={28} />
      <Skeleton variant="rounded" width={72} height={28} />
      <Skeleton variant="rounded" width={130} height={28} />
    </Stack>
    <Skeleton variant="text" width="72%" height={28} />
    <Stack spacing={0.5}>
      <Skeleton variant="text" width="92%" height={42} />
      <Skeleton variant="text" width="65%" height={42} />
    </Stack>
    <Skeleton variant="rounded" height={126} />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 1.5,
      }}>
      {Array.from({ length: 10 }, (_, index) => (
        <Skeleton key={index} variant="text" height={48} />
      ))}
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Skeleton
        variant="rounded"
        sx={{ width: { xs: '100%', sm: '58%' } }}
        height={40}
      />
      <Skeleton
        variant="rounded"
        sx={{ width: { xs: '100%', sm: '38%' } }}
        height={40}
      />
    </Stack>
    <Skeleton variant="rounded" height={64} />
    <Skeleton variant="rounded" height={60} />
  </Stack>
);

const DetailFacts = ({ facts }: { facts: DetailFact[] }) => {
  const availableFacts = facts.filter(
    (fact) =>
      fact.value !== null && fact.value !== undefined && fact.value !== '',
  );
  if (availableFacts.length === 0) return null;

  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 2,
        rowGap: 1.25,
        m: 0,
      }}>
      {availableFacts.map(({ label, value, href }) => (
        <Box
          component="div"
          key={label}
          sx={{
            minWidth: 0,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          <Typography component="dt" variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            fontWeight={600}
            sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {href ? (
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="inherit">
                {value}
              </Link>
            ) : (
              value
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DetailFieldGroup = ({
  title,
  facts,
}: {
  title: string;
  facts: DetailFact[];
}) => {
  const hasFacts = facts.some(
    (fact) =>
      fact.value !== null && fact.value !== undefined && fact.value !== '',
  );
  if (!hasFacts) return null;

  return (
    <Box
      component="section"
      aria-label={title}
      sx={(theme) => ({
        minWidth: 0,
        p: { xs: 1.5, sm: 2 },
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        bgcolor: theme.palette.grey[50],
      })}>
      <Typography
        component="h4"
        variant="subtitle2"
        fontWeight={700}
        color="text.secondary"
        sx={{ mb: 1.25 }}>
        {title}
      </Typography>
      <DetailFacts facts={facts} />
    </Box>
  );
};

const AccordionHeader = ({
  title,
  count,
}: {
  title: string;
  count: number;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1.5}
    sx={{ width: '100%', minWidth: 0 }}>
    <Typography component="span" variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Chip
      size="small"
      label={count}
      sx={(theme) => ({
        flexShrink: 0,
        fontWeight: 700,
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.dark,
      })}
    />
  </Stack>
);

const accordionSx = {
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { m: 0 },
} as const;

const TimelineDetailsContent = ({
  record,
  analysis,
  analysisStatus,
  operationScoped,
  summary,
  copyFeedbackMessage,
  onCopySummary,
}: TimelineDetailsContentProps) => {
  const cardTitle = getTimelineRecordTitle(record);
  const title =
    cardTitle ||
    getTimelineRecordTitle({
      evento: analysis?.event?.name,
      assunto: analysis?.subject,
      descricaofundo: analysis?.description,
      tipodocumento: analysis?.type,
    });
  const category = getValueText(analysis?.category, record.categoriadocumento);
  const market = getValueText(analysis?.market?.name, record.mercado);
  const relevance = getValueText(analysis?.relevance, record.relevancia);
  const referenceDate = getValueText(record.datareferencia, analysis?.date);
  const formattedReferenceDate = formatTimelineDate(referenceDate);
  const operationId = getValueText(
    analysis?.operation?.hashId,
    record.operationid,
  );
  const operationName = getValueText(
    analysis?.operation?.name,
    record.operationName,
  );
  const operationCnpj = formatCnpj(
    getValueText(analysis?.operation?.cnpj, analysis?.cnpj, record.cnpj),
  );
  const operationPath = getTimelineOperationPath({
    market,
    slug: getValueText(analysis?.operation?.slug, record.operationSlug),
    hashId: operationId,
  });
  const originalDocumentUrl =
    getHttpUrl(analysis?.document?.previewUrl) ||
    getHttpUrl(analysis?.document?.url) ||
    getHttpUrl(record.urlVisualizacao) ||
    getHttpUrl(record.url);
  const points = getDetailPoints(analysis?.analysis?.points ?? record.pontos);
  const citations = getCitations(
    analysis?.analysis?.citations ?? record.citacoes,
  );
  const citationSummary = analysis?.analysis?.citationsSummary;
  const exactCount =
    getCitationCount(
      citationSummary?.exact,
      record.citacoesExatas,
      citations.filter(
        (citation) => getCitationStatusKey(citation.status) === 'exata',
      ).length,
    ) ?? 0;
  const approximateCount =
    getCitationCount(
      citationSummary?.approximate,
      record.citacoesAproximadas,
      citations.filter(
        (citation) => getCitationStatusKey(citation.status) === 'aproximada',
      ).length,
    ) ?? 0;
  const notFoundCount =
    getCitationCount(
      citationSummary?.notFound,
      record.citacoesNaoLocalizadas,
      citations.filter((citation) => {
        const status = getCitationStatusKey(citation.status);
        return status === 'nao localizada' || status === 'não localizada';
      }).length,
    ) ?? 0;
  const citationTotal =
    getCitationCount(citationSummary?.total, record.citacoesTotal) ??
    citations.length;
  const hasCitationSection = citations.length > 0 || citationTotal > 0;
  const whyItMatters = getValueText(
    analysis?.whyItMatters,
    record.justificativaRelevancia,
  );
  const hasAnalysisContent = Boolean(
    summary || points.length > 0 || citations.length > 0 || whyItMatters,
  );
  const analysisUnavailable =
    analysisStatus === 'success' && !hasAnalysisContent;
  const futureWithoutAnalysis =
    analysisUnavailable && isTimelineDateAfterToday(referenceDate);

  const operationFacts: DetailFact[] = [
    { label: 'Mercado', value: market },
    { label: 'CNPJ do Emissor', value: operationCnpj },
  ];
  const classificationFacts: DetailFact[] = [
    {
      label: 'Tipo de documento',
      value: getValueText(analysis?.type, record.tipodocumento),
    },
    {
      label: 'Espécie',
      value: getValueText(analysis?.species, record.especiedocumento),
    },
    { label: 'Categoria', value: category },
    {
      label: 'Assunto',
      value: getValueText(analysis?.subject, record.assunto),
    },
    {
      label: 'Assuntos secundários',
      value: getValueText(
        analysis?.secondarySubjects,
        record.assuntosSecundarios,
      ),
    },
    {
      label: 'Evento (caminho)',
      value: getValueText(analysis?.event?.name, record.evento),
    },
    {
      label: 'Tipo do evento',
      value: getValueText(analysis?.event?.type, record.eventoTipo),
    },
    { label: 'Quórum', value: getValueText(analysis?.quorum, record.quorum) },
    {
      label: 'Realizada',
      value: getValueText(analysis?.held, record.realizada),
    },
    {
      label: 'Data de entrega',
      value: formatTimelineDate(
        getValueText(analysis?.deliveryDate, record.dataentrega),
      ),
    },
  ];

  return (
    <Stack spacing={2.5} data-testid="timeline-details-content">
      <Box component="section" aria-label="Operação">
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          {operationName && (
            <Typography
              component="h3"
              variant="h5"
              id="timeline-details-document-title"
              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {operationPath && !operationScoped ? (
                <Link
                  href={operationPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="primary.dark"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                  {operationName}
                  <OpenInNewRounded fontSize="small" aria-hidden="true" />
                </Link>
              ) : (
                operationName
              )}
            </Typography>
          )}
          {(title || formattedReferenceDate) && (
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              {title && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}>
                  {title}
                </Typography>
              )}
              {formattedReferenceDate && (
                <Stack
                  component="time"
                  dateTime={referenceDate}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ minWidth: 0 }}>
                  <CalendarTodayOutlined
                    aria-hidden="true"
                    sx={{ color: 'text.secondary', fontSize: '1rem' }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    Data de referência · {formattedReferenceDate}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
          <DetailFacts facts={operationFacts} />
        </Stack>
      </Box>

      {summary && (
        <Box
          component="section"
          aria-label="Resumo"
          sx={(theme) => ({
            p: { xs: 2, sm: 2.5 },
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.22),
            borderLeft: '4px solid',
            borderLeftColor: theme.palette.primary.main,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.055),
          })}>
          <Typography
            component="h4"
            variant="subtitle2"
            color="primary.dark"
            fontWeight={700}
            sx={{ mb: 0.75 }}>
            Resumo
          </Typography>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.65,
              overflowWrap: 'anywhere',
            }}>
            {summary}
          </Typography>
        </Box>
      )}

      {(originalDocumentUrl || summary) && (
        <Box
          component="section"
          aria-label="Ações do documento"
          sx={timelineDetailsActionGridSx}>
          {originalDocumentUrl && (
            <Button
              component="a"
              href={originalDocumentUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              endIcon={<OpenInNewRounded />}
              sx={timelineDetailsActionButtonSx}>
              Abrir documento original
            </Button>
          )}
          {summary && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              fullWidth
              startIcon={<ContentCopyRounded />}
              onClick={onCopySummary}
              sx={timelineDetailsActionButtonSx}>
              Copiar resumo
            </Button>
          )}
        </Box>
      )}
      {copyFeedbackMessage && (
        <Typography role="status" variant="body2" color="text.secondary">
          {copyFeedbackMessage}
        </Typography>
      )}

      {futureWithoutAnalysis && (
        <Alert severity="info" role="status">
          A análise documental ainda não está disponível para este evento
          futuro.
        </Alert>
      )}
      {analysisUnavailable && !futureWithoutAnalysis && (
        <Alert severity="info" role="status">
          Análise documental não disponível para este registro.
        </Alert>
      )}

      {(relevance || whyItMatters) && (
        <Box component="section" aria-label="Importância">
          <Typography
            component="h4"
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 1 }}>
            Importância
          </Typography>
          <Stack spacing={1.25}>
            {relevance && (
              <Box>
                <Chip
                  size="small"
                  label={`Relevância: ${relevance}`}
                  variant="outlined"
                  sx={{ maxWidth: '100%' }}
                />
              </Box>
            )}
            {whyItMatters && (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  lineHeight: 1.6,
                  overflowWrap: 'anywhere',
                }}>
                {whyItMatters}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {points.length > 0 && (
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="timeline-details-points-content"
            id="timeline-details-points-heading"
            sx={{ px: { xs: 1.5, sm: 2 }, minHeight: 60 }}>
            <AccordionHeader
              title="Pontos que merecem atenção"
              count={points.length}
            />
          </AccordionSummary>
          <AccordionDetails
            id="timeline-details-points-content"
            sx={{ pt: 0, px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <Stack
              component="ul"
              spacing={1}
              sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {points.map((point, index) => (
                <Box
                  component="li"
                  key={point.title + index}
                  sx={(theme) => ({
                    minWidth: 0,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderLeft: '3px solid',
                    borderLeftColor: alpha(theme.palette.primary.main, 0.65),
                    borderRadius: 2,
                    bgcolor: theme.palette.grey[50],
                  })}>
                  <Typography
                    component="h4"
                    variant="subtitle2"
                    fontWeight={700}>
                    {point.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      whiteSpace: 'pre-line',
                      overflowWrap: 'anywhere',
                    }}>
                    {point.content}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {hasCitationSection && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="timeline-details-citations-content"
            id="timeline-details-citations-heading"
            sx={{ px: { xs: 1.5, sm: 2 }, minHeight: 60 }}>
            <AccordionHeader title="Citações e fontes" count={citationTotal} />
          </AccordionSummary>
          <AccordionDetails
            id="timeline-details-citations-content"
            sx={{ pt: 0, px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`Exatas ${exactCount}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Aproximadas ${approximateCount}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Não localizadas ${notFoundCount}`}
              />
            </Stack>
            <Stack spacing={1.25}>
              {citations.map((citation, index) => (
                <Box
                  component="article"
                  key={citation.url || citation.excerpt || String(index)}
                  sx={(theme) => ({
                    minWidth: 0,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderLeft: '3px solid',
                    borderLeftColor: theme.palette.primary.light,
                    borderRadius: 2,
                    bgcolor: theme.palette.grey[50],
                  })}>
                  {citation.excerpt && (
                    <Typography
                      component="blockquote"
                      variant="body2"
                      sx={{ m: 0, overflowWrap: 'anywhere', lineHeight: 1.6 }}>
                      “{citation.excerpt}”
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {formatCitationStatus(citation.status)} ·{' '}
                      {citation.page
                        ? `página ${citation.page}`
                        : 'página não identificada'}
                    </Typography>
                    {citation.url && (
                      <Button
                        component="a"
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewRounded />}
                        sx={{
                          minWidth: 0,
                          px: 0.5,
                          whiteSpace: 'nowrap',
                          textTransform: 'none',
                        }}>
                        Abrir trecho
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <DetailFieldGroup
        title="Classificação do evento"
        facts={classificationFacts}
      />
    </Stack>
  );
};

export default function TimelineDetailsDrawer({
  open,
  record,
  navbarAdditionalHeight,
  onClose,
  operationId,
  operationScoped = false,
}: TimelineDetailsDrawerProps) {
  const [copyFeedback, setCopyFeedback] = useState<{
    recordId: number;
    message: string;
  } | null>(null);
  const {
    data: analysis,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ['timeline-analysis', operationId?.trim() || null, record?.id],
    queryFn: ({ signal }) =>
      record
        ? fetchTimelineAnalysis(record.id, signal)
        : Promise.reject(new Error('Registro da Timeline não selecionado')),
    enabled: open && Boolean(record?.id),
    staleTime: 5 * 60 * 1000,
  });
  let analysisStatus: TimelineAnalysisStatus = 'loading';
  if (isError) analysisStatus = 'error';
  else if (isSuccess) analysisStatus = 'success';
  const mobileNavbarHeight = 150 + navbarAdditionalHeight;
  const largerNavbarHeight = 75 + navbarAdditionalHeight;
  const summary = getTimelineRecordSummary(
    analysis?.summary || record?.resumo,
    analysis?.analysis?.points ?? record?.pontos,
  );
  const feedbackMessage =
    record && copyFeedback?.recordId === record.id ? copyFeedback.message : '';

  const handleCopySummary = async () => {
    if (!record || !summary) return;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard indisponível');
      }
      await navigator.clipboard.writeText(summary);
      setCopyFeedback({
        recordId: record.id,
        message: 'Resumo copiado.',
      });
    } catch {
      setCopyFeedback({
        recordId: record.id,
        message: 'Não foi possível copiar o resumo.',
      });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'timeline-details-heading',
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100vw', sm: 560, lg: 680 },
          maxWidth: '100vw',
          top: { xs: mobileNavbarHeight, sm: largerNavbarHeight },
          bottom: 0,
          height: 'auto',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        },
      }}>
      <Stack
        component="header"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          p: { xs: 1.5, sm: 2 },
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
        <Typography
          id="timeline-details-heading"
          component="h2"
          variant="h6"
          sx={{ minWidth: 0 }}>
          Detalhes do documento
        </Typography>
        <Button
          aria-label="Fechar detalhes do documento"
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<CloseRounded />}
          sx={{
            flexShrink: 0,
            borderColor: 'divider',
            color: 'text.secondary',
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}>
          Fechar
        </Button>
      </Stack>

      <Stack
        component="main"
        spacing={2}
        aria-busy={isLoading}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: { xs: 1.5, sm: 2.5 },
        }}>
        {isLoading && !analysis && <TimelineDetailsSkeleton />}
        {isError && (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void refetch()}>
                Tentar novamente
              </Button>
            }>
            Não foi possível carregar os detalhes do documento.
          </Alert>
        )}
        {!isLoading && record && (
          <TimelineDetailsContent
            record={record}
            analysis={analysis}
            analysisStatus={analysisStatus}
            operationScoped={operationScoped}
            summary={summary}
            copyFeedbackMessage={feedbackMessage}
            onCopySummary={() => void handleCopySummary()}
          />
        )}
      </Stack>
    </Drawer>
  );
}
